if (!customElements.get('cart-echantillons')) {
class CartEchantillons extends HTMLElement {
  connectedCallback() {
    const cards = this.querySelectorAll('[data-product-id]');
    if (!cards.length) return;

    this.initToggle();

    // Initialiser les selects de variantes sur chaque card
    cards.forEach((card) => this.initVariantSelects(card));

    this.initWheelScroll();

    // Empêcher les clics multiples : quand un bouton échantillon est cliqué,
    // désactiver immédiatement tous les AUTRES boutons jusqu'au re-render du drawer
    this.querySelectorAll('.cart_echantillons_addtocart').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.querySelectorAll('.cart_echantillons_addtocart').forEach((other) => {
          if (other !== btn) {
            other.setAttribute('aria-disabled', 'true');
            other.style.pointerEvents = 'none';
          }
        });
      });
    });

    const country = this.dataset.country;
    const shop = this.dataset.shop;
    if (!country || !shop) return;

    const productIds = [...cards].map((card) => card.dataset.productId);
    this.checkAvailability(shop, country, productIds);
  }

  // ---------- Repli / dépli ----------

  // Le drawer est re-rendu à chaque modification du panier : l'état replié
  // est mémorisé en sessionStorage pour survivre au re-render.
  static STORAGE_KEY = 'cart_echantillons_collapsed';

  initToggle() {
    const button = this.querySelector('.cart_echantillons__title');
    if (!button) return;

    // Par défaut : replié sur mobile (même seuil que le SCSS), déplié sur desktop.
    // Le choix du client pendant la session l'emporte ensuite.
    let collapsed = window.matchMedia('(max-width: 749px)').matches;
    try {
      const stored = sessionStorage.getItem(CartEchantillons.STORAGE_KEY);
      if (stored !== null) collapsed = stored === '1';
    } catch (e) {
      /* sessionStorage indisponible (navigation privée stricte) : défaut selon l'écran */
    }
    this.setCollapsed(collapsed, button);

    button.addEventListener('click', () => {
      const next = !this.classList.contains('is-collapsed');
      this.setCollapsed(next, button);
      try {
        sessionStorage.setItem(CartEchantillons.STORAGE_KEY, next ? '1' : '0');
      } catch (e) {
        /* idem */
      }
    });
  }

  setCollapsed(collapsed, button) {
    this.classList.toggle('is-collapsed', collapsed);
    button.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  }

  // ---------- Molette ----------

  // La molette verticale ne pilote pas nativement un scroll horizontal :
  // on la convertit tant que la zone peut encore défiler, et on rend la main
  // au drawer dès qu'elle est en butée (sinon la page se retrouve bloquée).
  initWheelScroll() {
    const track = this.querySelector('.cart_echantillons__products');
    if (!track) return;

    track.addEventListener(
      'wheel',
      (event) => {
        // Geste horizontal (trackpad, shift+molette) : déjà géré nativement
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

        const maxScroll = track.scrollWidth - track.clientWidth;
        if (maxScroll <= 1) return;

        // deltaMode 1 = défilement par lignes, 2 = par pages
        let delta = event.deltaY;
        if (event.deltaMode === 1) delta *= 16;
        else if (event.deltaMode === 2) delta *= track.clientWidth;

        const atStart = delta < 0 && track.scrollLeft <= 0;
        const atEnd = delta > 0 && track.scrollLeft >= maxScroll - 1;
        if (atStart || atEnd) return;

        event.preventDefault();
        track.scrollLeft = Math.min(maxScroll, Math.max(0, track.scrollLeft + delta));
      },
      { passive: false }
    );
  }

  // ---------- Variantes ----------

  initVariantSelects(card) {
    const selects = card.querySelectorAll('.cart_echantillons__select');
    if (!selects.length) return;

    const variants = JSON.parse(card.dataset.variants || '[]');

    // Filtrer les options indisponibles au chargement
    selects.forEach((select) => this.filterUnavailableOptions(select, selects, variants));

    selects.forEach((select) => {
      select.addEventListener('change', () => {
        // Quand une option change, re-filtrer les autres selects puis mettre à jour le variant
        selects.forEach((s) => this.filterUnavailableOptions(s, selects, variants));
        this.updateVariant(card, selects, variants);
      });
    });

    // Initialiser avec la première variante disponible
    this.updateVariant(card, selects, variants);
  }

  filterUnavailableOptions(targetSelect, allSelects, variants) {
    const optionIndex = parseInt(targetSelect.dataset.optionIndex, 10);

    // Récupérer les valeurs sélectionnées des autres selects
    const selectedOptions = [...allSelects].map((s) => (s === targetSelect ? null : s.value));

    // Pour chaque option du select cible, vérifier si une variante dispo existe
    [...targetSelect.options].forEach((option) => {
      const testOptions = [...selectedOptions];
      testOptions[optionIndex] = option.value;

      const hasAvailable = variants.some((v) => {
        return v.available && testOptions.every((val, i) => val === null || v.options[i] === val);
      });

      option.disabled = !hasAvailable;
      option.hidden = !hasAvailable;
    });

    // Si l'option sélectionnée est désormais désactivée, choisir la première dispo
    if (targetSelect.selectedOptions[0]?.disabled) {
      const firstEnabled = [...targetSelect.options].find((o) => !o.disabled);
      if (firstEnabled) targetSelect.value = firstEnabled.value;
    }
  }

  updateVariant(card, selects, variants) {
    const selectedOptions = [...selects].map((s) => s.value);

    const matchingVariant = variants.find(
      (v) => v.available && v.options.every((opt, i) => opt === selectedOptions[i])
    );

    const input = card.querySelector('input[name="id"]');
    const button = card.querySelector('.cart_echantillons_addtocart');

    if (matchingVariant) {
      input.value = matchingVariant.id;
      button.disabled = false;
    } else {
      button.disabled = true;
    }
  }

  // ---------- Dispo par marché (Storefront API) ----------

  async checkAvailability(shop, country, productIds) {
    const query = `
      query checkAvailability($ids: [ID!]!) @inContext(country: ${country}) {
        nodes(ids: $ids) {
          ... on Product {
            id
            availableForSale
          }
        }
      }
    `;

    const variables = {
      ids: productIds.map((id) => `gid://shopify/Product/${id}`),
    };

    try {
      const response = await fetch(`https://${shop}/api/2026-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '5fd2f533827bd3e8435eb388d5a8d063',
        },
        body: JSON.stringify({ query, variables }),
      });

      const { data } = await response.json();
      if (!data?.nodes) return;

      data.nodes.forEach((node) => {
        if (!node || node.availableForSale) return;

        const numericId = node.id.split('/').pop();
        const card = this.querySelector(`[data-product-id="${numericId}"]`);
        if (card) card.remove();
      });

      // Plus aucun échantillon disponible dans ce marché : masquer le bloc entier
      if (!this.querySelector('[data-product-id]')) this.remove();
    } catch (e) {
      // En cas d'erreur réseau, on laisse les cards visibles — pas de faux négatif
      console.error('[CartEchantillons] Storefront API error:', e);
    }
  }
}

customElements.define('cart-echantillons', CartEchantillons);
}
